import { AlertCircle, Loader2 } from 'lucide-react';
import { AuthGuard } from '@/components/auth/auth-guard';
import { AppLayout } from '@/components/layout';
import { useMyDashboard } from '@/hooks/use-dashboard';
import { DASHBOARD_WIDGET_COMPONENTS } from './widget-registry';
import { WidgetBoundary } from './widget-boundary';
import { asArray } from './widget-format';

/**
 * Home-screen Dashboard. Renders the signed-in user's assigned widgets in order,
 * skipping any key that has no registered frontend component, so an assigned-but-unbuilt
 * widget never breaks the live dashboard. Each card also renders inside its own error
 * boundary, so a widget whose payload drifts from its type costs that one card and not
 * the grid around it.
 */
export function DashboardPage() {
  const { data: widgets, isLoading, isError } = useMyDashboard();

  // This read happens above the per-widget boundaries, so it has to survive any payload:
  // `asArray` drops rows that are not there, and `Object.hasOwn` — rather than a plain lookup —
  // keeps a key like `toString` from resolving to an inherited member and being rendered as a
  // component. A row that is not an object has no `key` to match, so it falls out here too.
  const rendered = asArray(widgets).filter((w) =>
    Object.hasOwn(DASHBOARD_WIDGET_COMPONENTS, w.key)
  );

  return (
    <AuthGuard>
      <AppLayout>
        {/* Page header — breadcrumb, consistent with the other modules (Inventory etc.). */}
        <header className="sticky top-14 z-30 flex h-12 items-center border-b border-border bg-background/95 px-6 backdrop-blur supports-[backdrop-filter]:bg-background/60">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span>Home</span>
            <span>/</span>
            <span className="font-medium text-foreground">Dashboard</span>
          </div>
        </header>

        <div className="surface-ambient space-y-6 p-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-24 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : isError && !widgets ? (
            <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed border-destructive/40 py-16 text-center text-muted-foreground">
              <AlertCircle className="h-5 w-5 text-destructive" />
              <p className="text-sm">Couldn&apos;t load your dashboard. Please try again.</p>
            </div>
          ) : rendered.length === 0 ? (
            <div className="rounded-lg border border-dashed border-border py-16 text-center text-muted-foreground">
              <p className="text-sm">Your dashboard widgets will appear here soon.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
              {rendered.map((w) => {
                const Widget = DASHBOARD_WIDGET_COMPONENTS[w.key];
                return (
                  <WidgetBoundary key={w.key} title={w.name}>
                    <Widget />
                  </WidgetBoundary>
                );
              })}
            </div>
          )}
        </div>
      </AppLayout>
    </AuthGuard>
  );
}
